import { DeleteOutlined } from '@ant-design/icons';
import { Button, Modal, notification, Table, Tooltip, Typography } from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import type { ColumnsType } from 'antd/es/table';
import React, { useState } from 'react';
import { CardTypeResponse } from '../../model/Card-type.model';
import CardTypeService from '../../service/card-type-service/Card-type.service';
import EditCardTypeModal from './EditCardTypeModal';

const { Text } = Typography;

interface CardTypeListTableProps {
  cardTypeList: CardTypeResponse[];
  onSuccess: () => void;
}

const CardTypeListTable: React.FC<CardTypeListTableProps> = ({
  cardTypeList,
  onSuccess,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const screens = useBreakpoint();

  const handleDelete = async () => {
    if (deleteId !== null) {
      try {
        await CardTypeService.deleteCardType(deleteId);
        onSuccess();
        notification.success({ message: 'Xóa loại thẻ thành công!' });
      } catch (error: any) {
        console.error('Error deleting card type:', error);
        notification.error({
          message: error.message || 'Có lỗi xảy ra khi xóa loại thẻ!',
        });
      } finally {
        setDeleteId(null);
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns: ColumnsType<CardTypeResponse> = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 70,
      render: (_text, _record, index) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Tên loại thẻ',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          {text}
          {!screens.sm && (
            <div className="text-xs text-gray-500 mt-1">{record.bankName}</div>
          )}
        </div>
      ),
    },

    {
      title: 'Hành động',
      key: 'action',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <EditCardTypeModal cardType={record} onSuccess={onSuccess} />
          <Tooltip title="Xóa">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteId(record.cardTypeId)}
              className="flex items-center justify-center"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Table
        columns={columns}
        dataSource={cardTypeList}
        rowKey="cardTypeId"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: cardTypeList.length,
          onChange: handlePageChange,
          showSizeChanger: false,
          className: 'mt-4',
          position: ['bottomRight'],
        }}
        className="w-full border border-gray-100 shadow-md rounded-lg overflow-hidden"
        rowClassName="odd:bg-white even:bg-gray-50 hover:bg-gray-100"
        size="middle"
      />

      <Modal
        title="Xác nhận xóa"
        open={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteId(null)}>
            Hủy
          </Button>,
          <Button key="delete" type="primary" danger onClick={handleDelete}>
            Xóa
          </Button>,
        ]}
      >
        <p>
          Bạn có chắc chắn muốn xóa loại thẻ này không? Hành động này không thể
          hoàn tác.
        </p>
      </Modal>
    </div>
  );
};

export default CardTypeListTable;
